import { useEffect, useMemo, useState } from "react";
import styles from "./FilterDrawer.module.scss";
import Button from "../../common/Button/Button";
import { useTranslation } from "react-i18next";
import Icon from "../../common/Icon/Icon";
import { useAppSelector } from "../../../redux/hooks";
import SideDrawer from "../../common/SideDrawer/SideDrawer";
import closeIconLight from "../../../assets/images/LightTheme/close_icon_light.svg";
import closeIconDark from "../../../assets/images/DarkTheme/close_icon_dark.svg";
import FilterCategories from "./FilterCategories/FilterCategories";
import {
  FilterProviderProps,
  FiltersProvider,
  FilterState,
  initialFilterState,
  useFiltersContext,
} from "./FilterContext/FilterContext";
import FilterOptions from "./FilterOptions/FilterOptions";
import Checkbox from "./Checkbox/Checkbox";
import { useCreateAboAssignViewsMutation, useDeleteAboAssignViewMutation, useUpdateAboAssignViewsMutation } from "../../../redux/services/customiseViewAboAssignmentAPI";
import { useLazyGetViewDetailsQuery } from "../../../redux/services/customizeViewAPI";
import { useDispatch } from "react-redux";
import { updateViewAboAssignmentId } from "../../../redux/slices/aboAssignmentViewSlice";
import { InputFilterItem, KeyMapAboAssign } from "../../../types/types";
import { ABOAssignKeyMap } from "../../../constants/dataConstants";

const FilterDrawerStyles: React.CSSProperties = {
  width: "752px",
  padding: 0,
  height: "100vh",
};
let originalFilterState: FilterState = [];

const FilterDrawerWrapper: React.FC<FilterProviderProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentTheme: any = useAppSelector(
    (state: any) => state.theme.currentTheme
  );
  const [isSaveViewOpen, setIsSaveViewOpen] = useState(false);

  const onModalClose = () => {
    setOpen(false);
    setIsSaveViewOpen(false);
  };

  const onSaveView = () => {
    setIsSaveViewOpen(true);
  };

  const onSaveViewModalClose = () => {
    setIsSaveViewOpen(false);
  };

  const openFilterDrawer = () => {
    setOpen(true);
    originalFilterState = props?.filterState;
  }

  return (
    <>
      <div className={styles.customize_btn} onClick={openFilterDrawer}>
        <Icon
          name={"filter"}
          size={14}
          className={styles.filter_icon}
          color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
        />
        <span className={styles.label}>{t("filter")}</span>
      </div>
      <SideDrawer
        showDrawer={open}
        onClose={onModalClose}
        customStyle={FilterDrawerStyles}
      >
        <FiltersProvider {...props}>
          {isSaveViewOpen ? (
            <SaveViewDrawer
              {...props}
              onBack={onSaveViewModalClose}
              onClose={onModalClose}
            />
          ) : (
            <FilterDrawer
              {...props}
              onClose={onModalClose}
              onSaveView={onSaveView}
            />
          )}
        </FiltersProvider>
      </SideDrawer>
    </>
  );
};

const FilterDrawer: React.FC<
  FilterProviderProps & { onClose: () => void; onSaveView: () => void, savedViews: ViewListDataType[], }
> = ({
  filterState: savedFilterState,
  setFilterState: saveFilters,
  onClose,
  onSaveView,
  savedViews,
  setViewDetails, clearPaginationData, setToastMessage, closingMonth
}) => {
    const { t } = useTranslation();
    const currentTheme: any = useAppSelector(
      (state: any) => state.theme.currentTheme
    );

    const closeIcon = currentTheme === "dark" ? closeIconDark : closeIconLight;

    const { filterState, setFilterState } = useFiltersContext();
    const dispatch = useDispatch();
    const [updateView, { isLoading: isLoadingUpdateView }] =
      useUpdateAboAssignViewsMutation();

    const [isFilterUpdated, setIsFilterUpdate] = useState<boolean>(false);

    const viewId: any = useAppSelector(
      (state: any) => state.viewIdDetailsAboAssignment?.viewId
    );
    const defaultViewId = viewId?.defaultViewId
      ? viewId?.defaultViewId
      : viewId?.selectedViewId;

    const filterCount = useMemo(
      () => filterState.reduce((count, state) => count + state.values.length, 0),
      [filterState]
    );
    const selectedView = savedViews?.filter((view) => view.viewId === defaultViewId);

    const onReset = () => {
      setFilterState(initialFilterState);
    };

    const onCancel = () => {
      setFilterState(savedFilterState);
      onClose();
    };
    useEffect(() => {
      const isModified = isFilterModified(originalFilterState, filterState);
      setIsFilterUpdate(filterCount > 0 && isModified);
    }, [filterState]);

    const onSaveChanges = async () => {
      if (!isFilterUpdated) return;
      onClose();
      const convertedDataForPayload = kayMappedConvertedDataForPayload(filterState, ABOAssignKeyMap);
      const otherDetails = { aboAssignmentColumns: [], aboAssignmentSortColumn: [] };
      const formattedViewSeting = {
        ...otherDetails,
        ...convertedDataForPayload
      };
      const UpdateViewPayload = {
        type: "abo-assignment",
        name: selectedView[0]?.name,
        _default: selectedView[0]?._default,
        viewSetting: formattedViewSeting,
        description: "",
        viewId: defaultViewId
      };

      const response: any = await updateView({
        payload: UpdateViewPayload,
      });

      if (response?.data?.statusCode === 200) {
        setToastMessage("Filters updated successfully", "success");
        setViewDetails(formattedViewSeting);
        dispatch(
          updateViewAboAssignmentId({
            ...viewId,
            viewIdDetailsAboAssignment: {
              defaultViewId: response?.data?.body?.viewId,
              selectedViewId: null,
            },
          })
        );
      } else if (response?.data?.statusCode === 400) {
        setToastMessage(response?.error?.message || "Failed to update filterrs", "error");
        console.error("View Update failed");
      }
    };

    const onApply = () => {
      const convertedDataForPayload = kayMappedConvertedDataForPayload(filterState, ABOAssignKeyMap);
      const otherDetails = { aboAssignmentColumns: [], aboAssignmentSortColumn: [] };
      const formattedViewSeting = {
        ...otherDetails,
        ...convertedDataForPayload
      };

      clearPaginationData();
      setViewDetails(formattedViewSeting);      
      dispatch(
        updateViewAboAssignmentId({
          viewIdDetailsAboAssignment: {
            defaultViewId: null,
            selectedViewId: 0,
          },
        })
      );
      onClose();
    };

    return (
      <div className={`filter_drawer_container ${styles.container}`}>
        <div className={`filter_drawer_header ${styles.header}`}>
          <div className={`filter_drawer_heading ${styles.heading}`}>
            {t("filter")} {filterCount > 0 ? `(${filterCount}) ${selectedView[0]?.name ? `${t("For")} ${selectedView[0]?.name}` : ""}` : ""}
          </div>
          <div className={styles.reset}>
            <span onClick={onReset}>{t("reset")}</span>
          </div>
          <div className={styles.close_icon} onClick={onCancel}>
            <img src={closeIcon} alt="close" width="10px" height="11px" />
          </div>
        </div>
        <div className={`filter_drawer_content ${styles.content}`}>
          <div
            className={`filter_drawer_filter_container ${styles.filter_container}`}
          >
            <div
              className={`filter_drawer_categories_header ${styles.filter_header}`}
            >
              <h2>{t("filter_categories")}</h2>
              <p>{t("filter_categories_desc")}</p>
            </div>
            <FilterCategories />
          </div>
          <div
            className={`filter_drawer_options_container ${styles.options_container}`}
          >
            <FilterOptions closingMonth={closingMonth} />
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.apply_buttons}>
            <div className={`${styles.save_and_apply} ${!isFilterUpdated ? styles.disabled : ''} `} onClick={!isFilterUpdated ? () => { } : onSaveView}>
              <span className={styles.plus}>+ </span>
              {t("save_as_new_view")}
            </div>
            <Button
              className={styles.apply_btn}
              label={t("save_changes")}
              onClick={onSaveChanges}
              isDisabled={!defaultViewId || !isFilterUpdated}
            />
            <Button label={t("apply_but_dont_save")} onClick={onApply} style="filled" className={styles.apply_save_btn} />
          </div>
        </div>
      </div>
    );
  };

const SaveViewDrawer: React.FC<
  FilterProviderProps & { onBack: () => void; onClose: () => void }
> = ({ setFilterState: saveFilters, onBack, onClose, setViewDetails, clearPaginationData }) => {
  const { t } = useTranslation();
  const currentTheme: any = useAppSelector(
    (state: any) => state.theme.currentTheme
  );
  const dispatch = useDispatch();
  const closeIcon = currentTheme === "dark" ? closeIconDark : closeIconLight;

  const [viewName, setViewName] = useState("");
  const [isDefaultView, setIsDefaultView] = useState(false);

  const viewId: any = useAppSelector(
    (state: any) => state.viewIdDetailsAboAssignment?.viewId
  );

  const defaultViewId = viewId?.defaultViewId
    ? viewId?.defaultViewId
    : viewId?.selectedViewId;

  const { filterState } = useFiltersContext();

  const onCancel = () => {
    onBack();
  };

  const [createView, { isLoading: isLoadingCreateView }] =
    useCreateAboAssignViewsMutation();

  const onApply = () => {
    saveFilters(filterState);
    // call set view details and the update API.
    handleSaveView(createView); // defaultViewId ? updateView : createView
    onClose();
  };

  const handleSaveView = async (saveViewAPI: any) => {
    onClose();
    const convertedDataForPayload = kayMappedConvertedDataForPayload(filterState, ABOAssignKeyMap);
    const otherDetails = { aboAssignmentColumns: [], aboAssignmentSortColumn: [] };
    const formattedViewSeting = {
      ...otherDetails,
      ...convertedDataForPayload
    };
    const createViewPayload = {
      type: "abo-assignment",
      name: viewName,
      _default: isDefaultView,
      viewSetting: formattedViewSeting,
      description: "",
    };
    const response: any = await saveViewAPI({
      payload: createViewPayload,
    });

    if (response?.data?.statusCode === 200) {

      dispatch(
        updateViewAboAssignmentId({
          ...viewId,
          viewIdDetailsAboAssignment: {
            defaultViewId: null,
            selectedViewId: response?.data?.body?.viewId,
          },
        })
      );

      if (defaultViewId) {
        clearPaginationData();
        setViewDetails(formattedViewSeting);
      }
    } else if (response?.data?.statusCode === 400) {
      console.error("Not allow to have view with duplicate name");
    }
    // }
  };

  return (
    <div className={`filter_drawer_container ${styles.container}`}>
      <div className={`filter_drawer_header ${styles.header}`}>
        <div className={`filter_drawer_heading ${styles.heading}`}>
          {t("create_new_view")}
        </div>
        <div className={styles.reset} />
        <div className={styles.close_icon} onClick={onCancel}>
          <img src={closeIcon} alt="close" width="10px" height="11px" />
        </div>
      </div>
      <div className={`filter_drawer_content ${styles.content}`}>
        <div className={`filter_save_container ${styles.save_container}`}>
          <h2>{t("save_view_for_later")}</h2>
          <p>{t("save_view_for_later_desc")}</p>
          <input
            type="text"
            className={`drawer_input_text ${styles.input}`}
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            placeholder={t("name_of_view")}
          />
          <div className={styles.checkbox_wrapper}>
            <Checkbox
              label={t("set_as_default_view")}
              checked={isDefaultView}
              checkboxHandler={setIsDefaultView}
            />
          </div>
          <div className={styles.apply_buttons}>
            <Button
              className={styles.apply_btn}
              label={t("back")}
              onClick={onCancel}
            />
            <Button
              label={t("save_and_apply")}
              onClick={onApply}
              style="filled"
              isDisabled={!viewName.trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterDrawerWrapper;


const kayMappedConvertedDataForPayload = <T = any>(
  array: InputFilterItem<T>[],
  keyMap: KeyMapAboAssign
): Record<string, T[]> =>
  array.reduce((acc: Record<string, T[]>, item) => {
    const mappedKey = keyMap[item.key];
    if (!mappedKey) return acc;
    acc[mappedKey] = item.values.map((v: any) => {
      if (typeof v === "string") return v;
      if (v?.value) {
        return v?.value;
      }
      return v;
    });

    return acc;
  }, {});

const isFilterModified = (oldState: FilterState | [], newState: FilterState | []) => {
  if (oldState.length !== newState.length) return true;

  return oldState.some((oldItem) => {
    const newItem = newState.find(n => n.key === oldItem.key);
    if (!newItem) return true; // missing key means modified
    if (oldItem.values.length !== newItem.values.length) return true;

    return oldItem.values.some((v, i) => v !== newItem.values[i]);
  });
}